import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  TextField, 
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Collapse,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CancelIcon from '@mui/icons-material/Cancel';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import StraightenIcon from '@mui/icons-material/Straighten';
import TimerIcon from '@mui/icons-material/Timer';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PaymentsIcon from '@mui/icons-material/Payments';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SearchIcon from '@mui/icons-material/Search';
import VideocamIcon from '@mui/icons-material/Videocam';
import BoltIcon from '@mui/icons-material/Bolt';
import BarChartIcon from '@mui/icons-material/BarChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Config } from '../../config/config';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Updated list of video files with .mp4 extension
const videoFiles = [
  '/videos/300651D1-AF19-4DD4-9125-1F9D24360DED-4822027604279998768.mp4',
  '/videos/DAE6738C-7D36-420F-B74F-B3C62CB267D0-5233895343715553598.mp4',
  '/videos/229F9BFB-A60E-4D65-8B6A-2936F72CCF4C4417516672089792346.mp4',
  '/videos/clip_ab766fac-6d14-4833-87dc-0721e806c80d.mp4',
  '/videos/clip_70e0f59e-4115-4a99-8aa6-6982a03be8c7.mp4',
  '/videos/clip_746cc4f3-5be9-4b4a-acc5-d7663d41b21e.mp4',
  '/videos/clip_be3903bc-6433-406c-aa39-0e211dbdd00b.mp4',
];

// Poster images for each video (use the same filename with .jpg extension)
const videoPosterFiles = [
  '/video-thumbs/300651D1-AF19-4DD4-9125-1F9D24360DED-4822027604279998768.jpg',
  '/video-thumbs/DAE6738C-7D36-420F-B74F-B3C62CB267D0-5233895343715553598.jpg',
  '/video-thumbs/229F9BFB-A60E-4D65-8B6A-2936F72CCF4C4417516672089792346.jpg',
  '/video-thumbs/clip_ab766fac-6d14-4833-87dc-0721e806c80d.jpg',
  '/video-thumbs/clip_70e0f59e-4115-4a99-8aa6-6982a03be8c7.jpg',
  '/video-thumbs/clip_746cc4f3-5be9-4b4a-acc5-d7663d41b21e.jpg',
  '/video-thumbs/clip_be3903bc-6433-406c-aa39-0e211dbdd00b.jpg',
];

// Fallback poster if specific poster is not available
const fallbackPoster = '/video-thumbs/default-video-thumb.jpg';

const Landing: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const sliderRef = useRef<Slider>(null); // Ref for slider instance
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [showRevenueLossInfo, setShowRevenueLossInfo] = useState(false);
  const [openTestDialog, setOpenTestDialog] = useState(false);
  const [testForm, setTestForm] = useState({
    testName: '',
    testCompany: '',
    testPosition: '',
    testPhone: '',
    testEmail: '',
    testAddress: '',
    testMessage: '',
    testConsent: false,
  });
  const [isTestSubmitting, setIsTestSubmitting] = useState(false);
  // Add form validation state
  const [formErrors, setFormErrors] = useState({
    testName: '',
    testCompany: '',
    testPhone: '',
    testEmail: '',
    testAddress: '',
    testConsent: ''
  });
  // Add state for account creation success information
  const [accountCreated, setAccountCreated] = useState(false);
  const [accountInfo, setAccountInfo] = useState<{
    email: string;
    displayName: string;
    password: string;
  } | null>(null);

  // Scroll to hash fragment if present in URL
  useEffect(() => {
    // Get the hash from the URL (e.g., #kontakt)
    const hash = window.location.hash;
    
    // If a hash exists and it points to an element
    if (hash) {
      // Small delay to ensure DOM is fully loaded
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          // Scroll to the element
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSnackbar({
      open: true,
      message: '',
      severity: 'success'
    });

    try {
      console.log('[DEBUG] Contact Form Data:', contactForm);
      console.log('[DEBUG] Config.apiRoot:', Config.apiRoot);
      console.log('[DEBUG] Full request URL:', `${Config.apiRoot}/api/contact/send`);
      console.log('[DEBUG] Request Headers:', {
        'Content-Type': 'application/json',
      });
      
      const response = await fetch(`${Config.apiRoot}/api/contact/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm),
      });

      console.log('[DEBUG] Response Status:', response.status);
      console.log('[DEBUG] Response Headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[DEBUG] Error Response:', errorText);
        throw new Error(`Failed to send message: ${response.status} ${errorText}`);
      }

      const responseData = await response.json();
      console.log('[DEBUG] Response Data:', responseData);

      setSnackbar({
        open: true,
        message: 'Message sent successfully!',
        severity: 'success'
      });
      setContactForm({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to send message. Please try again.',
        severity: 'error'
      });
      console.error('Error sending message:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Snackbar schließen
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // FAQ Daten
  const faqItems = [
    {
      question: 'Welche Kameras werden für WashProTech benötigt?',
      answer: 'Die Kameras müssen in einem 90°-Winkel zur Kollisionsachse (Kettenbahn) ausgerichtet sein, einen Abstrahlwinkel von 120° und eine feste Brennweite von 2,8 mm haben. Die Installationshöhe sollte zwischen 3,0 und 4,5 Metern liegen.'
    },
    {
      question: 'Wie weit müssen die Kameras voneinander entfernt sein?',
      answer: 'Um eine lückenlose Abdeckung zu gewährleisten, sollte der Abstand zwischen den Kameras maximal 6 Meter betragen. Wir empfehlen ein Raster von ca. 5 Metern für optimale Ergebnisse.'
    },
    {
      question: 'Welche technischen Voraussetzungen gibt es für die Installation?',
      answer: 'Es werden feste IP-Adressen für die Kameras und die SPS benötigt. Zudem ist eine Anpassung der bestehenden Waschstraßen-Steuerung notwendig. Aktuell wird nur das System von Holz Autowaschtechnik unterstützt.'
    },
    {
      question: 'Wie hoch ist der ROI (Return on Investment) des Systems?',
      answer: 'Basierend auf unseren Berechnungen kann das System jährlich etwa 15.840 € an Umsatzausfällen und Personalkosten einsparen. Bei durchschnittlich 4-6 Unfällen pro Monat amortisiert sich das System in der Regel innerhalb von 12-18 Monaten.'
    },
    {
      question: 'Wie funktioniert die Erkennung?',
      answer: 'Die KI analysiert die Bilder der Kameras in Echtzeit und erkennt potenzielle Kollisionen durch die Identifizierung von Fahrzeugpositionen und -bewegungen. Bei einer drohenden Kollision wird automatisch ein Notstopp eingeleitet, um Schäden zu vermeiden.'
    },
    {
      question: 'Kann das System in bestehende Waschstraßen nachgerüstet werden?',
      answer: 'Ja, das System kann in die meisten bestehenden Waschstraßen integriert werden. Bei älteren Anlagen kann jedoch eine Anpassung der Steuerung notwendig sein. Unsere Techniker führen vorab eine Analyse durch, um die Kompatibilität zu prüfen.'
    }
  ];

  // Settings for react-slick
  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: isMobile ? '10%' : '25%', // Show parts of next/prev slides
    focusOnSelect: true, // Go to slide on click
    arrows: false, // Added to hide navigation arrows
    afterChange: (current: number) => { // Added type annotation for current
      // iOS has strict policies for video autoplay, so we'll handle it differently
      console.log("Slide changed to:", current);
      // We don't auto-play videos on slide change as this causes issues on iOS
    },
  };

  // Add a manual play handler for videos
  const handleVideoPlay = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    // Pause all other videos when one starts playing
    const currentVideo = event.currentTarget;
    const videos = document.querySelectorAll('#video-showcase .video-slide video');
    videos.forEach((video) => {
      const videoElement = video as HTMLVideoElement;
      if (videoElement !== currentVideo) {
        videoElement.pause();
      }
    });
  };

  const handleTestFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTestForm(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle Select change events separately
  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setTestForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form fields
    const errors = {
      testName: testForm.testName.trim() ? '' : 'Name ist erforderlich',
      testCompany: testForm.testCompany.trim() ? '' : 'Unternehmen ist erforderlich',
      testPhone: testForm.testPhone.trim() ? '' : 'Telefonnummer ist erforderlich',
      testEmail: !testForm.testEmail.trim() ? 'E-Mail ist erforderlich' : 
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testForm.testEmail) ? 'Ungültige E-Mail-Adresse' : '',
      testConsent: testForm.testConsent ? '' : 'Zustimmung zur Datenverarbeitung ist erforderlich',
      testAddress: ''  // Address is now optional
    };
    
    // Check if there are any errors
    const hasErrors = Object.values(errors).some(error => error !== '');
    setFormErrors(errors);
    
    if (hasErrors) {
      setSnackbar({
        open: true,
        message: 'Bitte füllen Sie alle Pflichtfelder korrekt aus.',
        severity: 'error'
      });
      return;
    }
    
    setIsTestSubmitting(true);
    // Reset account creation state
    setAccountCreated(false);
    setAccountInfo(null);

    try {
      console.log('[DEBUG] Test Form Data:', testForm);
      console.log('[DEBUG] Config.apiRoot:', Config.apiRoot);
      
      // Use the new endpoint that creates account and Pi together
      const apiUrl = `${Config.apiRoot.replace('/api', '')}/api/customer-info/test-with-account`;
      console.log('[DEBUG] Full request URL:', apiUrl);
      console.log('[DEBUG] Request Headers:', {
        'Content-Type': 'application/json',
      });
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testForm),
      });

      console.log('[DEBUG] Response Status:', response.status);
      console.log('[DEBUG] Response Headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[DEBUG] Error Response:', errorText);
        
        // Try to parse the error as JSON to extract detailed validation messages
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            // Display the server error message
            setSnackbar({
              open: true,
              message: errorData.message,
              severity: 'error'
            });
            
            // If there are validation errors in the response, update form errors
            if (errorData.errors) {
              const serverErrors = {
                testName: '',
                testCompany: '',
                testPhone: '',
                testEmail: '',
                testAddress: '',
                testConsent: ''
              };
              
              // Map backend error fields to frontend fields
              Object.entries(errorData.errors).forEach(([key, value]) => {
                const fieldName = key.replace('test', 'test'); // This is just for safety, in case field names differ
                if (serverErrors.hasOwnProperty(fieldName)) {
                  serverErrors[fieldName as keyof typeof serverErrors] = value as string;
                }
              });
              
              setFormErrors(prev => ({...prev, ...serverErrors}));
            }
            return;
          }
        } catch (parseError) {
          // If not a JSON response, use the original error text
          console.error('[DEBUG] Error parsing JSON response:', parseError);
        }
        
        throw new Error(`Failed to send message: ${response.status} ${errorText}`);
      }

      const responseData = await response.json();
      console.log('[DEBUG] Response Data:', responseData);

      // Check if account was created
      if (responseData.account) {
        setAccountCreated(true);
        setAccountInfo({
          email: responseData.account.email,
          displayName: responseData.account.displayName,
          password: responseData.account.password
        });
        
        setSnackbar({
          open: true,
          message: 'Test request sent successfully! Account was created.',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Test request sent successfully!',
          severity: 'success'
        });
      }
      
      // Don't reset the form or close the dialog when account is created
      // so the user can see the credentials
      if (!responseData.account) {
        setTestForm({
          testName: '',
          testCompany: '',
          testPosition: '',
          testPhone: '',
          testEmail: '',
          testAddress: '',
          testMessage: '',
          testConsent: false,
        });
        
        // Close test dialog after successful submission
        setOpenTestDialog(false);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to send test request. Please try again.',
        severity: 'error'
      });
      console.error('Error sending test request:', err);
    } finally {
      setIsTestSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box id="demo" sx={{ 
        background: 'linear-gradient(135deg, #000 0%, #333 100%)',
        color: 'white',
        py: 10,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: { xs: '30%', md: '55%' },
          left: { xs: '5%', md: '15%' },
          width: { xs: '500px', md: '600px' },
          height: { xs: '500px', md: '600px' },
          backgroundImage: 'url(/Wash-Pro-tech-vector.svg)',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          opacity: 0.3,
          transform: 'translateY(-50%)',
          zIndex: 0,
          filter: 'grayscale(100%) brightness(200%)'
        }
      }}>
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button 
              variant="outlined" 
              size="medium" 
              startIcon={<ArrowForwardIosIcon />}
              onClick={handleLoginClick}
              sx={{ 
                borderRadius: '8px',
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            > Anmelden
            </Button>
          </Box>
          
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h2" 
                component="h1" 
                sx={{ 
                  fontWeight: 700,
                  mb: 2,
                  fontSize: isMobile ? '2.5rem' : '3.5rem',
                  textShadow: '0 0 10px rgba(0,0,0,0.5)'
                }}
              >
                WashProTech
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  mb: 3,
                  fontWeight: 300,
                  fontSize: isMobile ? '1.5rem' : '2rem',
                  textShadow: '0 0 10px rgba(0,0,0,0.5)'
                }}
              >
                Kollisionsschutz für Waschstraßen
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 4, 
                  maxWidth: '600px',
                  textShadow: '0 0 10px rgba(0,0,0,0.5)'
                }}
              >
                Maximieren Sie die Sicherheit und Effizienz Ihrer Waschanlage durch unsere 
                intelligente Kollisionserkennung. Reduzieren Sie Unfälle, vermeiden Sie 
                Betriebsunterbrechungen und steigern Sie Ihren Umsatz.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  size="large" 
                  startIcon={<BoltIcon sx={{ color: '#30D158' }} />}
                  endIcon={<ArrowForwardIosIcon />}
                  sx={{ 
                    borderRadius: '8px',
                    px: 4,
                    py: 1.5,
                    backgroundColor: 'white',
                    color: 'black',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: '#f0f0f0'
                    }
                  }}
                  href="#testraum"
                >
                  3 Monate testen
                </Button>
                <Button 
                  variant="outlined" 
                  size="large" 
                  sx={{ 
                    borderRadius: '8px',
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                  href="#kontakt"
                >
                  Kontakt
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
              <Box 
                component="video"
                autoPlay
                muted
                loop
                playsInline
                sx={{
                  width: '100%',
                  borderRadius: '12px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                }}
              >
                <source src="/washprotech-demo.mp4" type="video/mp4" />
                Ihr Browser unterstützt keine Videos.
              </Box>
              <Paper 
                elevation={3} 
                sx={{ 
                  position: 'absolute', 
                  bottom: -20, 
                  right: isMobile ? 20 : 40, 
                  p: 2, 
                  borderRadius: '8px',
                  backgroundColor: '#30D158',
                  color: 'white',
                  width: isMobile ? '80%' : '60%'
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  Bis zu 15.840 € jährliche Einsparungen durch vermiedene Unfälle und Betriebsunterbrechungen!
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Problemstellung & Lösung */}
      <Box id="funktionen" sx={{ backgroundColor: '#f9f9f9', py: 8 }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h2" gutterBottom align="center">
                Die Herausforderung
              </Typography>
              <Typography variant="body1" paragraph align="center">
                Jeder Unfall in einer Waschstraße führt zu erheblichen Problemen, zudem leidet das Markenimage, wenn es regelmäßig zu Problemen kommt.
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                mb: 3, 
                p: 2, 
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                maxWidth: '90%',
                mx: 'auto'
              }}>
                <Box sx={{ color: '#30D158', mr: 2, mt: 0.5 }}>
                  <AttachMoneyIcon />
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Kundenverluste
                  </Typography>
                  <Typography variant="body2">
                    Wiederholte Unfälle und Verzögerungen können Kunden abschrecken und zu langfristigen Umsatzeinbußen führen.
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                mb: 3, 
                p: 2, 
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                maxWidth: '90%',
                mx: 'auto'
              }}>
                <Box sx={{ color: '#30D158', mr: 2, mt: 0.5 }}>
                  <TimerIcon />
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Betriebsunterbrechungen
                  </Typography>
                  <Typography variant="body2">
                    3-5 Minuten Stillstand pro Unfall, Verlust von ca. 15 Fahrzeugwäschen à 12 € Umsatz
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                p: 2, 
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                maxWidth: '90%',
                mx: 'auto'
              }}>
                <Box sx={{ color: '#30D158', mr: 2, mt: 0.5 }}>
                  <AccessTimeIcon />
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Zeitverlust für Mitarbeiter
                  </Typography>
                  <Typography variant="body2">
                    1-2 Stunden Arbeitszeit pro Mitarbeiter für die Bearbeitung jedes Unfalls
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6} sx={{ mt: { xs: 4, md: 0 } }}>
              <Typography variant="h3" component="h2" gutterBottom align="center">
                Unsere Lösung
              </Typography>
              <Typography variant="body1" paragraph align="center">
              WashProTech bietet eine umfassende Lösung für mehr Sicherheit und Effizienz:
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                mb: 3, 
                p: 2, 
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                maxWidth: '90%',
                mx: 'auto'
              }}>
                <Box sx={{ color: '#30D158', mr: 2, mt: 0.5 }}>
                  <PsychologyIcon />
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Früherkennung
                  </Typography>
                  <Typography variant="body2">
                    Intelligente Erkennung potenzieller Kollisionen, bevor sie passieren
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                mb: 3, 
                p: 2, 
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                maxWidth: '90%',
                mx: 'auto'
              }}>
                <Box sx={{ color: '#30D158', mr: 2, mt: 0.5 }}>
                  <CancelIcon />
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Automatische Notabschaltung
                  </Typography>
                  <Typography variant="body2">
                    Sofortige Reaktion zur Vermeidung von Schäden und Betriebsunterbrechungen
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                p: 2, 
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                maxWidth: '90%',
                mx: 'auto'
              }}>
                <Box sx={{ color: '#30D158', mr: 2, mt: 0.5 }}>
                  <BarChartIcon />
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Datenbasierte Optimierung
                  </Typography>
                  <Typography variant="body2">
                    Kontinuierliche Verbesserung durch Analyse von Betriebsdaten und Ereignissen
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Video Showcase Section - Using react-slick */}
      <Box id="video-showcase" sx={{ 
        py: 6, // Reduced vertical padding
        backgroundColor: '#f9f9f9',
        position: 'relative', // Needed for absolute positioning of dots
        overflowX: 'hidden', // Added to prevent horizontal scrolling
        // Add some global styles for slick adjustments if needed
        '& .slick-slide': {
          transition: 'transform 0.5s ease-in-out',
          padding: isMobile ? '0 5px' : '0 15px', 
          verticalAlign: 'middle', // Ensure proper vertical alignment
          boxSizing: 'border-box', // Ensure padding is included in width calculation
        },
        '& .slick-list': {
          overflow: 'visible',
        },
        '& .slick-center .video-slide': { 
          transform: 'scale(1.05)', 
          opacity: 1,
        },
        '& .slick-slide:not(.slick-center) .video-slide': {
          transform: 'scale(0.9)',
          opacity: 0.6, 
        },
        // Style dots container and active dot
        '& .slick-dots li.slick-active button:before': {
           color: theme.palette.primary.main, // Active dot color
           opacity: 1,
        },
        '& .slick-dots li button:before': {
            fontSize: '10px', // Dot size
            color: 'grey.400', // Inactive dot color
            opacity: 0.7,
        },
      }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ mb: 4 /* Reduced margin bottom */ }}>
          WashProTech in Aktion
          </Typography>
          
          {/* Removed the extra wrapper Box */}
          <Slider ref={sliderRef} {...sliderSettings}>
            {videoFiles.map((videoSrc, index) => (
              <Box key={index} className="video-slide" sx={{ outline: 'none' }}>
                <Box 
                  component="video"
                  controls 
                  playsInline
                  width="100%"
                  poster={videoPosterFiles[index] || fallbackPoster}
                  sx={{ 
                    display: 'block', 
                    borderRadius: '12px',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                    maxWidth: '100%' // Ensure video doesn't overflow slide padding
                  }}
                  src={videoSrc}
                  preload="none" // Prevent auto-preloading of videos
                  onPlay={handleVideoPlay}
                >
                  Ihr Browser unterstützt dieses Videoformat nicht. Bitte verwenden Sie MP4.
                </Box>
              </Box>
            ))}
          </Slider>
          
          {/* Added CTA Button below slider */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button 
              variant="contained" 
              size="large" 
              href="#kontakt"
              sx={{ 
                borderRadius: '8px',
                px: 4,
                py: 1.5
              }}
            >
              Mehr Sicherheit durch KI: Jetzt anfragen!
            </Button>
          </Box>
          
        </Container>
      </Box>

      {/* Testraum Angebot Section */}
      <Box id="testraum" sx={{ 
        py: 8, 
        backgroundColor: '#000000', 
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          right: '5%',
          width: '300px',
          height: '300px',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          opacity: 0.2,
          transform: 'translateY(-50%)',
          zIndex: 0,
          filter: 'brightness(200%)'
        }
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12}>
              <Typography 
                variant="h3" 
                component="h2" 
                sx={{ 
                  fontWeight: 700,
                  mb: 2,
                  fontSize: isMobile ? '2rem' : '2.5rem',
                  textAlign: 'center'
                }}
              >
                <span style={{ color: '#30D158' }}> Ausfahrtssicherung </span> testen
              </Typography>
              
              <Typography variant="h6" sx={{ mb: 4, fontWeight: 400, textAlign: 'center' }}>
                Testen Sie WashProTech mit minimaler Anfangsinvestition
              </Typography>
              
              <Box sx={{ 
                p: 4, 
                backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                borderRadius: '12px',
                backdropFilter: 'blur(5px)',
                mb: 4,
                maxWidth: '900px',
                mx: 'auto'
              }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, borderBottom: '1px solid rgba(255,255,255,0.2)', pb: 1 }}>
                        Im Testpaket enthalten
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ mr: 2, color: '#ffffff' }}>
                          <VideocamIcon />
                        </Box>
                        <Typography variant="body1">
                          Lizenz für 2 Ausfahrts Kameras
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ mr: 2, color: '#ffffff' }}>
                          <SearchIcon />
                        </Box>
                        <Typography variant="body1">
                          Waschtunnel-Übersicht für Einweiser
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ mr: 2, color: '#ffffff' }}>
                          <BoltIcon />
                        </Box>
                        <Typography variant="body1">
                          Echtzeit-Systemausschaltung bei Abstandsproblemen
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ mr: 2, color: '#ffffff' }}>
                          <MenuBookIcon />
                        </Box>
                        <Typography variant="body1">
                          Videoarchiv der Unfallsituationen
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Box sx={{ mr: 2, color: '#ffffff' }}>
                          <BarChartIcon />
                        </Box>
                        <Typography variant="body1">
                          Statistiken über den Waschbetrieb
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" sx={{ mt: 3, fontStyle: 'italic' }}>
                        <strong>Hinweis zu Kameras:</strong> Wir stellen detaillierte Spezifikationen für die benötigten Kameras bereit. 
                        Die Installation und Beschaffung der Kameras erfolgt durch den Kunden.
                      </Typography>
                      
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        <strong>Kompatibilität:</strong> Derzeit unterstützen wir nur Waschstraßen mit Holz Anlagen. Weitere folgen.
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ 
                      p: 0,
                      pt: 0,
                      pb: 3,
                      px: 3,
                      backgroundColor: '#ffffff', 
                      color: '#000000', 
                      borderRadius: '12px', 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div>
                        <Typography variant="h5" sx={{ fontWeight: 700, mt: 3, mb: 3, borderBottom: '2px solid #000', pb: 1 }}>
                          Transparente Preisstruktur
                        </Typography>

                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: 3 }}>
                          Monatliche Kosten:
                        </Typography>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1">1. Monat</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                textDecoration: 'line-through',
                                fontWeight: 'bold',
                                color: 'text.secondary',
                                mr: 1
                              }}
                            >
                              450 €
                            </Typography>
                            <Typography 
                              fontWeight="bold"

                              variant="body1" 
                              sx={{ color: '#30D158' }}
                            >
                              0 €
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1">2. Monat</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                textDecoration: 'line-through',
                                color: 'text.secondary',
                                mr: 1,
                                fontWeight: 'bold'
                              }}
                            >
                              450 €
                            </Typography>
                            <Typography 
                              variant="body1" 
                              fontWeight="bold"
                              sx={{ color: '#30D158' }}
                            >
                              0 €
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body1">3. Monat</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography 
                              variant="body1" 
                              fontWeight="bold"
                              sx={{ 
                                textDecoration: 'line-through',
                                color: 'text.secondary',
                                mr: 1
                              }}
                            >
                              450 €
                            </Typography>
                            <Typography 
                              variant="body1" 
                              fontWeight="bold"
                              sx={{ color: '#30D158' }}
                            >
                              0 €
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                          <Typography variant="body1">Ab 4. Monat</Typography>
                          <Typography variant="body1" fontWeight="bold">450 €**</Typography>
                        </Box>
                        
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          Einrichtungsgebühr:
                        </Typography>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1">Waschstraßensteuerung Update</Typography>
                          <Typography variant="body1" fontWeight="bold">399 €</Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1">WashProTech Router</Typography>
                          <Typography variant="body1" fontWeight="bold">499 €</Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, backgroundColor: 'rgba(0,0,0,0.05)', p: 1, borderRadius: '4px' }}>
                          <Typography variant="body1" fontWeight="bold">Einrichtungsgebühr gesamt</Typography>
                          <Typography variant="body1" fontWeight="bold">898 €</Typography>
                        </Box>
                        
                        <Typography variant="body2" sx={{ mb: 3, mt: 2, fontStyle: 'italic' }}>
                          *Einrichtungsgebühr von 898 € entfällt bei weiterer Nutzung*
                        </Typography>
                        
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: '#f8f8f8', 
                          borderRadius: '8px',
                          mb: 3,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                          borderLeft: '4px solid #30D158'
                        }}>
                          <Box sx={{ 
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            backgroundColor: '#30D158'
                          }} />
                          <Box sx={{ mr: 2, color: '#30D158' }}>
                            <BoltIcon fontSize="large" />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#000000', textAlign: 'center' }}>
                            3 Probemonate
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" sx={{ mb: 3, fontStyle: 'italic', color: 'text.secondary', textAlign: 'left', fontSize: '0.8rem' }}>
                          **450 € netto monatlich bei Abschluss eines Jahresabonnements, ansonsten 499 € netto pro Monat.**
                        </Typography>
                      </div>
                      
                      <Button 
                        variant="contained" 
                        size="large" 
                        onClick={() => {
                          document.getElementById('testraum-formular')?.scrollIntoView({ behavior: 'smooth' });
                          setOpenTestDialog(true);
                        }}
                        sx={{ 
                          backgroundColor: '#000000',
                          color: 'white',
                          border: '2px solid white',
                          px: 4,
                          py: 1.5,
                          '&:hover': {
                            backgroundColor: '#333333'
                          }
                        }}
                      >
                        Testinstallation anfragen
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              
              <Typography variant="body2" paragraph sx={{ textAlign: 'center', opacity: 0.7, maxWidth: '800px', mx: 'auto' }}>
                Starten Sie mit minimaler Investition und testen Sie die Wirksamkeit von WashProTech an der 
                kritischsten Stelle Ihrer Anlage. Bei Zufriedenheit können Sie problemlos auf die gesamte Waschanlage upgraden.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Vorteile Section */}
      <Container maxWidth="xl" sx={{ py: 8, backgroundColor: '#f9f9f9' }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ mb: 6 }}>
          Vorteile von WashProTech
          </Typography>
        
        <Grid container spacing={{ xs: 6, sm: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.08)', backgroundColor: '#ffffff' }}>
              <CardContent sx={{ textAlign: 'center', p: { xs: 4, sm: 3 } }}>
                <Box sx={{ color: '#30D158', mb: 2 }}>
                  <SearchIcon fontSize="large" />
                </Box>
                <Typography variant="h5" component="h3" gutterBottom>
                  Automatische Erkennung
          </Typography>
                <Typography variant="body2">
                  Früherkennung von potenziellen Kollisionen und Gefahrensituationen in Echtzeit.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.08)', backgroundColor: '#ffffff' }}>
              <CardContent sx={{ textAlign: 'center', p: { xs: 4, sm: 3 } }}>
                <Box sx={{ color: '#30D158', mb: 2 }}>
                  <PaymentsIcon fontSize="large" />
                </Box>
                <Typography variant="h5" component="h3" gutterBottom>
                  Kostenreduktion
                </Typography>
                <Typography variant="body2">
                  Vermeidung von Ausfallzeiten, Reparaturkosten und Personalaufwand durch weniger Unfälle.
                </Typography>
              </CardContent>
            </Card>
              </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.08)', backgroundColor: '#ffffff' }}>
              <CardContent sx={{ textAlign: 'center', p: { xs: 4, sm: 3 } }}>
                <Box sx={{ color: '#30D158', mb: 2 }}>
                  <VideocamIcon fontSize="large" />
                </Box>
                <Typography variant="h5" component="h3" gutterBottom>
                  Einfache Integration
                </Typography>
                <Typography variant="body2">
                  Nachrüstbar in bestehende Anlagen und kompatibel mit vorhandenen Kamerasystemen.
                </Typography>
              </CardContent>
            </Card>
              </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.08)', backgroundColor: '#ffffff' }}>
              <CardContent sx={{ textAlign: 'center', p: { xs: 4, sm: 3 } }}>
                <Box sx={{ color: '#30D158', mb: 2 }}>
                  <BoltIcon fontSize="large" />
                </Box>
                <Typography variant="h5" component="h3" gutterBottom>
                  Optimierter Betrieb
                </Typography>
                <Typography variant="body2">
                  Höhere Durchlaufrate und gesteigerte Kundenzufriedenheit durch reibungslosen Betrieb.
                </Typography>
              </CardContent>
            </Card>
              </Grid>
              </Grid>
      </Container>
      
      {/* Wirtschaftlichkeitsrechnung */}
      <Box sx={{ py: 8, backgroundColor: '#f9f9f9' }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ mb: 4 }}>
            Wirtschaftlichkeitsrechnung
          </Typography>
          
          <Typography variant="body1" align="center" paragraph sx={{ mb: 5 }}>
            Bei durchschnittlich 4-6 Unfällen pro Monat in einer Waschstraße entstehen folgende Kosten, die durch WashProTech vermieden werden können:
          </Typography>
          
          <Card sx={{ mb: 5, borderRadius: '12px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
            <Box sx={{ backgroundColor: '#000', color: 'white', p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Jährliche Kostenersparnis
              </Typography>
            </Box>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                <Grid container spacing={2}>
              <Grid item xs={12}>
                    <Grid container>
                      <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                          <Typography variant="body1" fontWeight="bold">Vermiedene Betriebsunterbrechungen</Typography>
                          <Box sx={{ display: { xs: 'block', md: 'none' }, ml: 'auto', mt: { xs: 1, md: 0 } }}>
                            <Typography variant="body1" fontWeight="bold" color="#30D158">
                              12.960 €
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          4-6 Unfälle/Monat × 180 € Umsatzverlust/Unfall × 12 Monate
                        </Typography>
              </Grid>
                      <Grid item xs={12} md={4} sx={{ 
                        display: { xs: 'none', md: 'flex' },
                        textAlign: 'right', 
                        alignItems: 'center', 
                        justifyContent: 'flex-end'
                      }}>
                        <Typography variant="body1" fontWeight="bold" color="#30D158">
                          12.960 €
                        </Typography>
              </Grid>
              </Grid>
                  </Grid>
                  
              <Grid item xs={12}>
                    <Box sx={{ 
                      mt: 1, 
                      p: 2, 
                      backgroundColor: 'rgba(0,0,0,0.02)', 
                      borderRadius: '8px', 
                      width: '100%',
                      boxSizing: 'border-box'
                    }}>
                      <Typography variant="body2" fontWeight="bold" gutterBottom>
                        Berechnung des Umsatzverlusts pro Unfall:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ mr: 1, color: '#30D158' }}>
                          <TimerIcon />
                        </Box>
                        <Typography variant="body2">3-5 Minuten Stillstand pro Unfall</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ mr: 1, color: '#30D158' }}>
                          <DirectionsCarIcon />
                        </Box>
                        <Typography variant="body2">Verlust von ca. 15 Fahrzeugwäschen</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1, color: '#30D158' }}>
                          <PaymentsIcon />
                        </Box>
                        <Typography variant="body2">à 12 € Umsatz pro Wäsche = 180 € Verlust pro Unfall</Typography>
                      </Box>
                    </Box>
              </Grid>
            </Grid>
          </Box>
              
              <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                <Grid container>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                      <Typography variant="body1" fontWeight="bold">Eingesparte Personalkosten</Typography>
                      <Box sx={{ display: { xs: 'block', md: 'none' }, ml: 'auto', mt: { xs: 1, md: 0 } }}>
                        <Typography variant="body1" fontWeight="bold" color="#30D158">
                          2.880 €
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      4-6 Unfälle/Monat × 2 Stunden × 40 €/Stunde × 12 Monate
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ 
                    display: { xs: 'none', md: 'flex' },
                    textAlign: 'right', 
                    alignItems: 'center', 
                    justifyContent: 'flex-end'
                  }}>
                    <Typography variant="body1" fontWeight="bold" color="#30D158">
                      2.880 €
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              
              <Box sx={{ p: 3, backgroundColor: 'rgba(48, 209, 88, 0.1)' }}>
                <Grid container>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                      <Typography variant="body1" fontWeight="bold">Gesamte jährliche Ersparnis</Typography>
                      <Box sx={{ display: { xs: 'block', md: 'none' }, ml: 'auto', mt: { xs: 1, md: 0 } }}>
                        <Typography variant="h6" fontWeight="bold" color="#30D158">
                          15.840 €
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ 
                    display: { xs: 'none', md: 'flex' },
                    textAlign: 'right', 
                    alignItems: 'center', 
                    justifyContent: 'flex-end'
                  }}>
                    <Typography variant="h6" fontWeight="bold" color="#30D158">
                      15.840 €
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
          
          <Box sx={{ textAlign: 'center' }}>
          <Button 
            variant="contained"
              size="large" 
              endIcon={<ArrowForwardIosIcon />}
              href="#kontakt"
            sx={{ 
              borderRadius: '8px', 
              px: 4,
                py: 1.5
            }}
          >
              Jetzt Beratungsgespräch vereinbaren
          </Button>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ backgroundColor: '#f9f9f9', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ mb: 4 }}>
            Häufig gestellte Fragen
          </Typography>
          
          {faqItems.map((item, index) => (
            <Accordion 
              key={index} 
              sx={{ 
                mb: 2, 
                borderRadius: '8px !important', 
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                backgroundColor: '#ffffff',
                '&:before': {
                  display: 'none'
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  backgroundColor: '#ffffff', 
                  '&.Mui-expanded': {
                    borderBottom: '1px solid rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Typography variant="body1" fontWeight="bold">{item.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">{item.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* Downloads Section */}
      <Box sx={{ py: 8, backgroundColor: '#f9f9f9' }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ mb: 4 }}>
            Downloads
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={6}>
              <Card sx={{ 
                height: '100%', 
                borderRadius: '12px', 
                boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#ffffff'
              }}>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                  <Box sx={{ color: '#30D158', mb: 2 }}>
                    <MenuBookIcon />
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom>
                  WashProTech Handbuch
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3 }}>
                    Handbuch mit allen wichtigen Informationen zur Installation und Nutzung von WashProTech.
                  </Typography>
                  <Button
                    variant="contained"
                    component="a"
                    href="/WashProTech_Handbuch_Kapitel_1_Kameras.pdf"
                    download="WashProTech_Handbuch_Kapitel_1_Kameras.pdf"
                    sx={{ 
                      borderRadius: '8px',
                      px: 4,
                      py: 1.5,
                      backgroundColor: '#000000',
                      '&:hover': {
                        backgroundColor: '#333333'
                      }
                    }}
                  >
                    Handbuch herunterladen
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card sx={{ 
                height: '100%', 
                borderRadius: '12px', 
                boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#ffffff'
              }}>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                  <Box sx={{ color: '#30D158', mb: 2 }}>
                    <StraightenIcon />
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom>
                    Grundriss Beispiel
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3 }}>
                    Beispiel-Grundriss für die optimale Anordnung der Kameras in Ihrer Waschanlage.
                  </Typography>
                  <Button
                    variant="contained"
                    component="a"
                    href="/GrundrissBeispielDownload.pdf"
                    download="GrundrissBeispielDownload.pdf"
                    sx={{ 
                      borderRadius: '8px',
                      px: 4,
                      py: 1.5,
                      backgroundColor: '#000000',
                      '&:hover': {
                        backgroundColor: '#333333'
                      }
                    }}
                  >
                    Grundriss herunterladen
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Kontakt Section - mit funktionalem E-Mail-Versand */}
      <Box id="kontakt" sx={{ py: 8, backgroundColor: '#f9f9f9' }}>
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h2" gutterBottom>
                Kontaktieren Sie uns
              </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                Interessiert an WashProTech für Ihre Waschanlage? Füllen Sie das Formular aus und ein Experte wird sich innerhalb von 24 Stunden bei Ihnen melden.
              </Typography>
              
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="name"
                      label="Name"
                      variant="outlined"
                      fullWidth
                      required
                      value={contactForm.name}
                      onChange={handleContactChange}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="email"
                      label="E-Mail"
                      variant="outlined"
                      fullWidth
                      required
                      type="email"
                      value={contactForm.email}
                      onChange={handleContactChange}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="phone"
                      label="Telefonnummer"
                      variant="outlined"
                      fullWidth
                      value={contactForm.phone}
                      onChange={handleContactChange}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="company"
                      label="Unternehmen"
                      variant="outlined"
                      fullWidth
                      value={contactForm.company}
                      onChange={handleContactChange}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="message"
                      label="Nachricht"
                      variant="outlined"
                      fullWidth
                      required
                      multiline
                      rows={4}
                      value={contactForm.message}
                      onChange={handleContactChange}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button 
                      type="submit" 
                      variant="contained" 
                      fullWidth
                      size="large"
                      disabled={isSubmitting}
                      sx={{ 
                        borderRadius: '8px', 
                        py: 1.5,
                        backgroundColor: '#000000',
                        '&:hover': {
                          backgroundColor: '#333333'
                        }
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Anfrage senden'
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Box 
                component="img" 
                src="/technician-tablet-landscape.jpg" 
                alt="WashProTech Techniker mit Tablet"
                sx={{
                  width: '100%',
                  maxHeight: '400px',
                  objectFit: 'cover',
                  borderRadius: '12px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Snackbar für Feedback */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Test Installation Dialog */}
      <Dialog
        open={openTestDialog}
        onClose={() => setOpenTestDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          px: 3, 
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #eaeaea'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BoltIcon sx={{ color: '#30D158', mr: 1.5 }} />
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
              Testinstallation anfragen
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setOpenTestDialog(false)}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {accountCreated && accountInfo ? (
            <Box sx={{ mt: 2 }}>
              <Alert 
                severity="success" 
                sx={{ mb: 3 }}
                icon={<CheckCircleIcon fontSize="large" />}
              >
                Ihre Testanfrage wurde erfolgreich gesendet und ein Zugang für Sie eingerichtet!
              </Alert>
              
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mb: 3, 
                  border: '1px solid #e0e0e0',
                  backgroundColor: '#f8f9fa'
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Ihre Zugangsdaten für WashProTech
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">Unternehmen:</Typography>
                    <Typography variant="body1" fontWeight="medium">{accountInfo.displayName}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">E-Mail:</Typography>
                    <Typography variant="body1" fontWeight="medium">{accountInfo.email}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">Passwort:</Typography>
                    <Typography variant="body1" fontWeight="medium">{accountInfo.password}</Typography>
                  </Grid>
                </Grid>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => {
                      const credentials = `E-Mail: ${accountInfo.email}\nPasswort: ${accountInfo.password}\nUnternehmen: ${accountInfo.displayName}`;
                      navigator.clipboard.writeText(credentials);
                      setSnackbar({
                        open: true,
                        message: 'Zugangsdaten in die Zwischenablage kopiert',
                        severity: 'success'
                      });
                    }}
                  >
                    Zugangsdaten kopieren
                  </Button>
                  
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    onClick={() => {
                      navigate('/login');
                    }}
                  >
                    Zum Login
                  </Button>
                </Box>
              </Paper>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Bitte bewahren Sie Ihre Zugangsdaten sicher auf. Sie können sich ab sofort mit diesen Daten im WashProTech-System anmelden.
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                Unser Team wird sich in Kürze mit Ihnen in Verbindung setzen, um die nächsten Schritte für Ihre Testinstallation zu besprechen.
              </Typography>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleTestSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="testName"
                    label="Name"
                    variant="outlined"
                    fullWidth
                    required
                    value={testForm.testName}
                    onChange={handleTestFormChange}
                    disabled={isTestSubmitting}
                    error={!!formErrors.testName}
                    helperText={formErrors.testName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="testEmail"
                    label="E-Mail"
                    variant="outlined"
                    fullWidth
                    required
                    type="email"
                    value={testForm.testEmail}
                    onChange={handleTestFormChange}
                    disabled={isTestSubmitting}
                    error={!!formErrors.testEmail}
                    helperText={formErrors.testEmail}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="testCompany"
                    label="Unternehmen"
                    variant="outlined"
                    fullWidth
                    required
                    value={testForm.testCompany}
                    onChange={handleTestFormChange}
                    disabled={isTestSubmitting}
                    error={!!formErrors.testCompany}
                    helperText={formErrors.testCompany}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="testPosition"
                    label="Position im Unternehmen"
                    variant="outlined"
                    fullWidth
                    value={testForm.testPosition}
                    onChange={handleTestFormChange}
                    disabled={isTestSubmitting}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="testPhone"
                    label="Telefonnummer"
                    variant="outlined"
                    fullWidth
                    required
                    value={testForm.testPhone}
                    onChange={handleTestFormChange}
                    disabled={isTestSubmitting}
                    error={!!formErrors.testPhone}
                    helperText={formErrors.testPhone}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="testAddress"
                    label="Standort der Waschanlage"
                    variant="outlined"
                    fullWidth
                    value={testForm.testAddress}
                    onChange={handleTestFormChange}
                    disabled={isTestSubmitting}
                    error={!!formErrors.testAddress}
                    helperText={formErrors.testAddress}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="testMessage"
                    label="Zusätzliche Informationen"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    value={testForm.testMessage}
                    onChange={handleTestFormChange}
                    disabled={isTestSubmitting}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="testConsent"
                        checked={testForm.testConsent}
                        onChange={(e) => {
                          setTestForm(prev => ({
                            ...prev,
                            testConsent: e.target.checked
                          }));
                          // Clear error when checked
                          if (e.target.checked) {
                            setFormErrors(prev => ({
                              ...prev,
                              testConsent: ''
                            }));
                          }
                        }}
                        disabled={isTestSubmitting}
                      />
                    }
                    label="Ich stimme zu, dass meine Daten zur Bearbeitung dieser Anfrage gespeichert und verarbeitet werden."
                    required
                  />
                  {formErrors.testConsent && (
                    <Typography color="error" variant="caption" sx={{ display: 'block', mt: -1, ml: 2 }}>
                      {formErrors.testConsent}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eaeaea' }}>
          {accountCreated && accountInfo ? (
            <Button 
              onClick={() => setOpenTestDialog(false)} 
              color="primary"
              variant="contained"
            >
              Schließen
            </Button>
          ) : (
            <>
              <Button 
                onClick={() => setOpenTestDialog(false)} 
                color="inherit"
                disabled={isTestSubmitting}
              >
                Abbrechen
              </Button>
              <Button 
                onClick={handleTestSubmit} 
                variant="contained" 
                color="primary" 
                disabled={isTestSubmitting || !testForm.testConsent}
                startIcon={isTestSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isTestSubmitting ? 'Wird gesendet...' : 'Anfrage senden'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Footer */}
      <Box sx={{ backgroundColor: '#000', color: 'white', py: 4 }}>
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                WashProTech
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Kollisionsschutz für Waschstraßen - Maximieren Sie die Effizienz und Sicherheit Ihrer Anlage.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Produkt
              </Typography>
              <Link href="#funktionen" color="inherit" sx={{ display: 'block', mb: 1, opacity: 0.7, textDecoration: 'none' }}>Funktionen</Link>
              <Link href="#testraum" color="inherit" sx={{ display: 'block', mb: 1, opacity: 0.7, textDecoration: 'none' }}>Testangebot</Link>
              <Link href="#demo" color="inherit" sx={{ display: 'block', mb: 1, opacity: 0.7, textDecoration: 'none' }}>Demo</Link>
            </Grid>
            
            {/* Unternehmen-Tab auskommentiert 
            <Grid item xs={12} md={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Unternehmen
              </Typography>
              <Link href="#" color="inherit" sx={{ display: 'block', mb: 1, opacity: 0.7 }}>Über uns</Link>
              <Link href="#" color="inherit" sx={{ display: 'block', mb: 1, opacity: 0.7 }}>Team</Link>
              <Link href="#" color="inherit" sx={{ display: 'block', mb: 1, opacity: 0.7 }}>Karriere</Link>
            </Grid>
            */}
            
            <Grid item xs={12} md={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Rechtliches
              </Typography>
              <a 
                href="/impressum" 
                style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  opacity: 0.7, 
                  color: 'inherit', 
                  textDecoration: 'none' 
                }}
              >
                Impressum
              </a>
              <a 
                href="/datenschutz" 
                style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  opacity: 0.7, 
                  color: 'inherit', 
                  textDecoration: 'none' 
                }}
              >
                Datenschutz
              </a>
              <a 
                href="/agb" 
                style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  opacity: 0.7, 
                  color: 'inherit', 
                  textDecoration: 'none' 
                }}
              >
                AGB
              </a>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Kontakt
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, opacity: 0.7 }}>info@washprotech.de</Typography>
              <Typography variant="body2" sx={{ mb: 1, opacity: 0.7 }}>+49 173 4700004</Typography>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3, backgroundColor: 'rgba(255,255,255,0.1)' }} />
          
          <Typography variant="body2" align="center" sx={{ opacity: 0.7 }}>
            © {new Date().getFullYear()} WashProTech
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Landing; 